import {SphereGeometry} from 'shared/geometries/SphereGeometry';
import {Planogram} from './planogram';
import {ItemData, MetaDataType} from './interfaces/planogram.interface';
import {
  Action,
  isPlanogramVersionCluster,
  isPlanogramVersionCurve,
  isPlanogramVersionImage,
  isPlanogramVersionProduct,
  isPlanogramVersionShape,
  isPlanogramVersionTextArea,
  isPlanogramVersionVideo,
  isProductOverlayAction,
  PlanogramVersionItem
} from 'shared/interfaces/planogram';
import {SphereItemType} from 'shared/interfaces/planogram';
import {Color, Object3D, Vector2, Vector3} from 'three';
import {SphereShape} from 'shared/utils/SphereShape';
import ImageObject3D from 'shared/object3d/ImageObject3D';
import ShapeObject3D from 'shared/object3d/ShapeObject3D';
import TextAreaObject3D from 'shared/object3d/TextAreaObject3D';
import VideoObject3D from 'shared/object3d/VideoObject3D';
import CurveObject3D from 'shared/object3d/CurveObject3D';
import LodProvider from 'shared/lod/LodProvider';
import {
  CurveObjectConfig,
  ImageObjectConfig,
  ShapeObjectConfig,
  TextAreaObjectConfig,
  VideoObjectConfig
} from 'shared/interfaces/object_config';
import PlanogramPoint from 'shared/utils/PlanogramPoint';
import {sphereEventHandler} from './custom_event_utils';
import {SPHERE_EVENT_NAMES} from './event-names';
import {Metrics} from './metrics';
import {AppState} from './shared/app.state';
import {MATOMO_EVENT_NAMES} from './metric-events';
import {PLANOGRAM_CONFIG} from 'shared/config/PlanogramConfig';

export const MAX_ITEM_RENDER_ORDER = 999999;

// TODO: reuse with editor/src/utils/rendering-engine.utils.ts
function subVirtualSphereY(y: number) {
  return y - PLANOGRAM_CONFIG.height / 2 - PLANOGRAM_CONFIG.virtualSphereY;
}

export class SphereItem implements PlanogramVersionItem {
  object3D: Object3D;
  planogram: Planogram;
  readonly type: SphereItemType;
  readonly id: string;
  readonly identifier: string;
  readonly name: string; // only set for images / products, isn't actually the name of the item in editor

  // TODO: make readonly after text rendering supports alignment
  readonly x: number;
  readonly y: number;
  width: number;
  height: number;

  protected azimuthStartRadians: number;
  action?: Action;
  data: MetaDataType;
  readonly layer: number; // necessary to implement PlanogramVersionItem
  readonly itemData: ItemData;
  readonly visible: boolean;

  overrideSize(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.updateAzimuthStartRadians();
  }

  private updateAzimuthStartRadians() {
    this.azimuthStartRadians = SphereGeometry.calcAzimuthStartRadians(
      this.x,
      Math.max(0, this.width),
      this.planogram.width
    );
  }

  constructor(params: ItemData, planogram: Planogram, lodProvider: LodProvider) {
    this.planogram = planogram;
    this.itemData = params;
    this.data = params.data;
    this.id = params.id;
    this.x = params.x;
    this.y = subVirtualSphereY(params.y);
    this.width = params.width;
    this.height = params.height;
    this.visible = params.visible;
    this.updateAzimuthStartRadians();

    this.layer = params.layer;

    if (params.action !== undefined && isProductOverlayAction(params.action)) {
      this.data.product = {
        id: params.action.data?.productId || this.data.product?.id,
        identifier: params.action.data?.productIdentifier || this.data.product?.identifier,
        name: params.action.data?.productName || this.data.product?.name
      };
    }

    if (params.data?.product?.name) {
      this.name = params.data.product.name;
    }

    if (params.data?.product?.identifier) {
      this.identifier = params.data.product.identifier;
    } else if (params.data?.product?.id) {
      this.identifier = params.data.product.id.toString();
    }

    if (params.action) {
      this.action = params.action;
    }

    this.type = params.type;

    this.createObject3D(lodProvider);
  }

  private createObject3D(lodProvider: LodProvider): void {
    const palette = this.planogram.palette;
    const itemData = this.itemData;
    const position = this.makePosition();
    const scale: [number, number, number] = [itemData.width, itemData.height, 1];
    if (isPlanogramVersionImage(itemData) || isPlanogramVersionProduct(itemData)) {
      const object3D = new ImageObject3D();
      const config: ImageObjectConfig = {
        ...itemData.data,
        builderData: {},
        position,
        scale
      };
      object3D.configure(config, lodProvider.getMaterial(this.itemData.id));
      this.object3D = object3D;
    } else if (isPlanogramVersionShape(itemData) || isPlanogramVersionCluster(itemData)) {
      const object3D = new ShapeObject3D();
      const config: ShapeObjectConfig = {
        ...itemData.data,
        builderData: {},
        position,
        scale,
        cornerRadius: itemData.data.individualCornerRadius
      };
      object3D.configure(config);
      object3D.rebuild(config, palette);
      this.object3D = object3D;
    } else if (isPlanogramVersionTextArea(itemData)) {
      const object3D = new TextAreaObject3D();
      const config: TextAreaObjectConfig = {
        ...itemData.data,
        builderData: {},
        position,
        scale,
        nameManuallyDefined: false
      };
      object3D.configure(config, palette);
      this.object3D = object3D;
    } else if (isPlanogramVersionCurve(itemData)) {
      const object3D = new CurveObject3D();
      const config: CurveObjectConfig = {
        ...itemData.data,
        builderData: {},
        position
      };
      // curve scale is not the same as object size
      scale.splice(0, undefined, ...itemData.data.scale);

      object3D.configure(config);
      object3D.rebuild(config, palette);
      this.object3D = object3D;
    } else if (isPlanogramVersionVideo(itemData)) {
      const object3D = new VideoObject3D();
      object3D.setPlaceholderColor(new Color('#ffffff'), 0);
      const config: VideoObjectConfig = {
        ...itemData.data,
        builderData: {},
        position,
        scale,
        fullSize: itemData.data.fullSize ?? false,
        naturalResolution: itemData.data.naturalResolution ?? [scale[0], scale[1]]
      };
      object3D.configure(config);
      object3D.hideControls(config.hideControls);
      object3D.addEventListener('VideoShare', () => {
        sphereEventHandler.emit(SPHERE_EVENT_NAMES.VIDEO.SHARE, this);
      });
      (object3D.userData as any).pointerdown = () => {
        Metrics.storeTheEvent(
          AppState.planogramName,
          'click',
          MATOMO_EVENT_NAMES.WEBGL_CLICK_VIDEO(itemData.name, itemData.data.videoUrl, Math.round(object3D.playTime))
        );
      };
      this.object3D = object3D;
    }
    // TODO: add Group and SocialFeed?
    else {
      throw new Error(`Unsupported item ${itemData.id} of type ${itemData.type}`);
    }

    this.object3D.position.set(position.x, position.y, 1);
    this.object3D.scale.set(...scale);
    this.object3D.renderOrder = MAX_ITEM_RENDER_ORDER - itemData.layer;
    this.object3D.visible = itemData.visible;
    this.object3D.userData.component = this;
  }

  hasInput() {
    return this.action !== undefined;
  }

  getPosition() {
    return new Vector2(this.x, this.y);
  }

  getSize() {
    return new Vector2(Math.abs(this.width), Math.abs(this.height));
  }

  getViewportCenter() {
    // TODO: why is the offset still necessary?
    return new Vector2(
      this.x + 0.5 * Math.abs(this.width),
      this.y + 0.5 * Math.abs(this.height) + this.planogram.height * 0.25
    );
  }

  makePosition() {
    return new PlanogramPoint({
      x: this.x + 0.5 * Math.abs(this.width),
      y: this.y + 0.5 * Math.abs(this.height)
    });
  }

  getCenter(sphereShape: SphereShape): [Vector3, Vector3] {
    const planogramCenter = this.getViewportCenter();
    const sphereCenter = sphereShape.fromPlanogramCoordinateViewer(planogramCenter, this.planogram.size());
    return [sphereShape.sample(sphereCenter), sphereShape.normalAt(sphereCenter).multiplyScalar(-1)];
  }

  dispose(): void {
    // @ts-ignore
    this.object3D?.dispose?.();
  }
}
